import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import "./CharS.css";
import {
  PET_CHARACTERS,
  RARITIES,
  createPetCard,
  getPacketFrames,
  resolveCardImage,
} from "./SitePetConfig";
import {
  getStoredPetCard,
  getPetStats,
  savePetCard,
  recordPetEvent,
  recordPetVisitOnce,
  recordActivePetTime,
  syncPetToSupabase,
} from "./SitePetProgress";
import { computePetPower } from "./SitePetPower";

const PENDING_CARD_KEY = "portfolio_pending_pet_card";
const FEATURED_REDIRECT = "#/featured-projects";

const getPendingCard = () => {
  try {
    return JSON.parse(localStorage.getItem(PENDING_CARD_KEY));
  } catch {
    return null;
  }
};

const savePendingCard = (card, signature) => {
  localStorage.setItem(
    PENDING_CARD_KEY,
    JSON.stringify({
      card,
      signature,
      savedAt: new Date().toISOString(),
    })
  );
};

const clearPendingCard = () => {
  localStorage.removeItem(PENDING_CARD_KEY);
};

function CharacterSelection() {
  const navigate = useNavigate();
  const cardRef = useRef(null);

  const [pendingSeed] = useState(() => getPendingCard());
  const [selected, setSelected] = useState(
    () => pendingSeed?.card?.characterId || "trex"
  );
  const [lockedCharacter, setLockedCharacter] = useState(
    () => pendingSeed?.card?.characterId || null
  );
  const [opening, setOpening] = useState(false);
  const [packetFrame, setPacketFrame] = useState(0);
  const [card, setCard] = useState(() => pendingSeed?.card || null);
  const [signature, setSignature] = useState(
    () => pendingSeed?.signature || pendingSeed?.card?.signature || ""
  );
  const [stats, setStats] = useState(() => getPetStats());

  const activeCharacter = lockedCharacter || selected;
  const profile = PET_CHARACTERS[activeCharacter] || PET_CHARACTERS.trex;

  const packetFrames = useMemo(
    () => getPacketFrames(activeCharacter),
    [activeCharacter]
  );

  const rarity = card ? RARITIES[card.rarity] || RARITIES.emerald : null;
  const power = useMemo(() => computePetPower(card, stats), [card, stats]);
  const characterLocked = opening || Boolean(card) || Boolean(lockedCharacter);

  const cardImageSrc = card
    ? card.cardImageSrc || resolveCardImage(card.cardImagePath)
    : "";

  const cardCode = card
    ? card.cardCode ||
      `${String(card.characterId || "CARD").toUpperCase()}-${String(
        card.rarity || "emerald"
      ).toUpperCase()}-${String(card.cardVariant || 1).padStart(2, "0")}`
    : "";

  const cardNumber = card
    ? `#${String(card.cardNumber || 0).padStart(3, "0")}`
    : "";

  useEffect(() => {
    if (getStoredPetCard()) {
      navigate("/featured-projects", { replace: true });
      return;
    }

    recordPetVisitOnce();

    const timer = window.setInterval(() => {
      setStats(recordActivePetTime(1000));
    }, 1000);

    return () => window.clearInterval(timer);
  }, [navigate]);

  useEffect(() => {
    if (card) {
      savePendingCard(card, signature.trim());
    }
  }, [card, signature]);

  useEffect(() => {
    if (!opening) {
      setPacketFrame(0);
      return undefined;
    }

    const timer = window.setInterval(() => {
      setPacketFrame((frame) => Math.min(frame + 1, 4));
    }, 260);

    return () => window.clearInterval(timer);
  }, [opening]);

  const handleCardTilt = useCallback((event) => {
    const node = cardRef.current;
    if (!node) return;

    const rect = node.getBoundingClientRect();
    const x = (event.clientX - rect.left) / rect.width;
    const y = (event.clientY - rect.top) / rect.height;

    node.style.setProperty("--tilt-x", `${(0.5 - y) * 14}deg`);
    node.style.setProperty("--tilt-y", `${(x - 0.5) * 18}deg`);
    node.style.setProperty("--glare-x", `${x * 100}%`);
    node.style.setProperty("--glare-y", `${y * 100}%`);
    node.classList.add("is-tilting");
  }, []);

  const resetCardTilt = useCallback(() => {
    const node = cardRef.current;
    if (!node) return;

    node.style.setProperty("--tilt-x", "0deg");
    node.style.setProperty("--tilt-y", "0deg");
    node.style.setProperty("--glare-x", "50%");
    node.style.setProperty("--glare-y", "50%");
    node.classList.remove("is-tilting");
  }, []);

  const openPack = () => {
    if (opening || card) return;

    const characterToOpen = selected;

    recordPetEvent("click");
    setLockedCharacter(characterToOpen);
    setOpening(true);
    setCard(null);

    window.setTimeout(() => {
      const nextCard = createPetCard(characterToOpen, signature.trim());

      setCard(nextCard);
      setOpening(false);
      recordPetEvent("powerup");
      setStats(getPetStats());
      savePendingCard(nextCard, signature.trim());
    }, 1900);
  };

  const activateCard = async () => {
    if (!card) return;

    const saved = savePetCard({
      ...card,
      signature: signature.trim(),
    });

    clearPendingCard();
    await syncPetToSupabase(saved);
    navigate("/featured-projects", { replace: true });
  };

  const skipToFeatured = () => {
    clearPendingCard();
    window.location.replace(FEATURED_REDIRECT);
  };

  const packetImage = packetFrames[packetFrame] || packetFrames[0] || "";

  return (
    <main className="character-selection">
      <section className="character-selection__panel">
        <div className="character-selection__left">
          <p className="character-selection__eyebrow">Choose your site pet</p>
          <h1>
            {characterLocked
              ? "Character locked. Claim your card."
              : "Pick a character. Open one card."}
          </h1>

          <div className="character-grid">
            {Object.values(PET_CHARACTERS).map((character) => (
              <button
                key={character.id}
                disabled={characterLocked}
                className={`character-choice ${
                  activeCharacter === character.id ? "is-selected" : ""
                } ${characterLocked ? "is-locked" : ""}`}
                onClick={() => {
                  if (characterLocked) return;
                  setSelected(character.id);
                  setCard(null);
                  recordPetEvent("click");
                }}
              >
                <span>{character.name}</span>
                <small>
                  {activeCharacter === character.id && characterLocked
                    ? "locked"
                    : character.personality}
                </small>
              </button>
            ))}
          </div>

          <label className="signature-field">
            <span>Sign your card</span>
            <input
              value={signature}
              maxLength={22}
              onChange={(e) => setSignature(e.target.value)}
              placeholder="your name"
            />
          </label>

          <div className="live-stats">
            <span>Visits {stats.visits}</span>
            <span>Clicks {stats.clicks}</span>
            <span>Scrolls {stats.scrolls}</span>
            <span>{Math.floor(stats.timeMs / 1000)}s stayed</span>
          </div>
        </div>

        <div className="character-selection__right">
          {!card ? (
            <button
              className={`card-pack ${opening ? "is-opening" : ""}`}
              onClick={openPack}
              disabled={opening}
              aria-label="Open card packet"
              style={{
                "--packet-image": packetImage ? `url(${packetImage})` : "none",
              }}
            >
              <span className="card-pack__image" />
              <span className="card-pack__split card-pack__split--left" />
              <span className="card-pack__split card-pack__split--right" />
              <svg
                className="card-pack__tear-svg"
                viewBox="0 0 100 140"
                preserveAspectRatio="none"
                aria-hidden="true"
              >
                <path d="M52 0 C 44 18, 60 34, 44 50 C 62 67, 39 84, 56 101 C 45 116, 59 128, 51 140" />
              </svg>
              <span className="card-pack__shine" />
              <span className="card-pack__title">{profile.name}</span>
              <span className="card-pack__subtitle">
                {opening ? "Opening..." : "Open packet"}
              </span>
            </button>
          ) : (
            <div className="revealed-card-wrap">
              <article
                ref={cardRef}
                className="pet-card pet-card--tilt"
                onMouseMove={handleCardTilt}
                onMouseLeave={resetCardTilt}
                style={{
                  "--rarity-color": rarity.color,
                  "--rarity-gradient": rarity.gradient,
                }}
              >
                {cardImageSrc && (
                  <img
                    className="pet-card__bg"
                    src={cardImageSrc}
                    alt=""
                    draggable="false"
                  />
                )}

                <div className="pet-card__content">
                  <div className="pet-card__top">
                    <span className="char-rarity">{card.rarityLabel}</span>
                    <span className="char-power2">{power} HP</span>
                  </div>

                  <div className="pet-card__art" />

                  <h2 className="char-name">{card.characterName}</h2>

                  <p className="pet-card__flavor">
                    {card.flavorLine || "No card sentence saved."}
                  </p>

                  <div className="pet-card__traits">
                    {card.traits.map((trait) => (
                      <span key={trait}>{trait}</span>
                    ))}
                  </div>

                  <div className="pet-card__meta">
                    <span className="card-code">
                      {cardCode} · {cardNumber}
                    </span>
                    <span className="card-sign">
                      {signature.trim() || "unsigned"}
                    </span>
                  </div>

                  <h5 className="card-code2">
                    © Ragav.me/Portfolio/CharacterCards/2026
                  </h5>
                </div>
              </article>

              <button className="card-next-button" onClick={activateCard}>
                Next
              </button>
            </div>
          )}
        </div>
      </section>
    </main>
  );
}

export default CharacterSelection;
